import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TagInput = ({ tags, value, onChange, tagType, maxTags = 250, maxLength = 255, placeholder = "Add tags..." }) => {
  // Ensure tags is always an array - accept both 'tags' and 'value' props for compatibility
  const tagArray = Array.isArray(tags) ? tags : (Array.isArray(value) ? value : []);
  
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  // Adjust max length based on tag type
  const charLimit = tagType === 'order' || tagType === 'draft_order' ? 40 : maxLength;

  // Fetch tag suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (inputValue.length > 0 && tagType) {
        try {
          const token = sessionStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/tags/`, {
            params: {
              tag_type: tagType,
              search: inputValue,
              limit: 10
            },
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          setSuggestions(response.data.filter(tag => 
            !tagArray.some(t => t.toLowerCase() === tag.name.toLowerCase())
          ));
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching tag suggestions:', error);
          // Don't block user from adding tags if API fails
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [inputValue, tagType, tagArray]);

  const validateTag = (tag) => {
    // Allow alphanumeric, hyphens, underscores, and spaces
    const regex = /^[a-zA-Z0-9\s\-_]+$/;
    if (!regex.test(tag)) {
      return 'Tag can only contain letters, numbers, spaces, hyphens, and underscores';
    }
    if (tag.length > charLimit) {
      return `Tag exceeds maximum length of ${charLimit} characters`;
    }
    return null;
  };

  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (!trimmedTag) return;

    // Validate tag
    const error = validateTag(trimmedTag);
    if (error) {
      alert(error);
      return;
    }

    // Check max tags
    if (tagArray.length >= maxTags) {
      alert(`Maximum ${maxTags} tags allowed`);
      return;
    }

    // Check for duplicates (case-insensitive)
    if (tagArray.some(t => t.toLowerCase() === trimmedTag.toLowerCase())) {
      return;
    }

    onChange([...tagArray, trimmedTag]);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeTag = (indexToRemove) => {
    onChange(tagArray.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tagArray.length > 0) {
      removeTag(tagArray.length - 1);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    addTag(suggestion.display_name || suggestion.name);
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] focus-within:ring-2 focus-within:ring-blue-500">
        {tagArray.map((tag, index) => {
          // Check if tag is an object with color or just a string
          const tagName = typeof tag === 'string' ? tag : tag.name;
          const tagColor = typeof tag === 'object' ? tag.color : null;
          
          return (
            <Badge 
              key={tagName || `tag-${index}`} 
              variant="secondary" 
              className="flex items-center gap-1 px-2 py-1"
              style={tagColor ? {
                backgroundColor: `${tagColor}20`,
                borderColor: tagColor,
                color: tagColor
              } : {}}
            >
              <span>{tagName}</span>
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tagArray.length === 0 ? placeholder : ''}
          className="flex-1 border-none outline-none focus-visible:ring-0 shadow-none min-w-[120px]"
          maxLength={charLimit}
        />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
            >
              <span>{suggestion.display_name || suggestion.name}</span>
              <Badge variant="outline" className="text-xs">
                {suggestion.usage_count} uses
              </Badge>
            </div>
          ))}
        </div>
      )}
      
      <p className="text-xs text-gray-500 mt-1">
        {tagArray.length}/{maxTags} tags • Press Enter or comma to add • Max {charLimit} characters per tag
      </p>
    </div>
  );
};

export default TagInput;
